import { useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { logErrorForDev } from "../../../utils";
import type { LocationSelectionResult } from "../../../utils/location/locationSelection";
import { locationSelectionFromPlacePrediction } from "../../../utils/location/locationSelection";

export interface LocationPlaceSearchProps {
    countryRestrictions: string[];
    disabled?: boolean;
    searchDisplayValue?: string;
    onPlaceSelected: (selection: LocationSelectionResult) => void;
}

const LocationPlaceSearch = ({
    countryRestrictions,
    disabled,
    searchDisplayValue = "",
    onPlaceSelected,
}: LocationPlaceSearchProps) => {
    const places = useMapsLibrary("places");
    const containerRef = useRef<HTMLDivElement>(null);
    const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    const userEditingSearchRef = useRef(false);
    onPlaceSelectedRef.current = onPlaceSelected;

    useEffect(() => {
        if (!places || !containerRef.current || disabled) return;

        const element = new places.PlaceAutocompleteElement({
            includedRegionCodes: countryRestrictions.length > 0 ? countryRestrictions : undefined,
            placeholder: "Search for a place",
            noInputIcon: true,
        });
        element.style.colorScheme = "light";
        autocompleteRef.current = element;
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(element);

        const handleFocusIn = () => {
            userEditingSearchRef.current = true;
        };
        const handleFocusOut = () => {
            window.setTimeout(() => {
                userEditingSearchRef.current = false;
            }, 200);
        };

        element.addEventListener("focusin", handleFocusIn);
        element.addEventListener("focusout", handleFocusOut);

        const handleSelect = async (event: google.maps.places.PlacePredictionSelectEvent) => {
            try {
                userEditingSearchRef.current = false;
                const placePrediction = event.placePrediction;
                if (!placePrediction) return;

                const place = placePrediction.toPlace();
                await place.fetchFields({
                    fields: ["displayName", "formattedAddress", "location", "addressComponents"],
                });

                const selection = locationSelectionFromPlacePrediction({ place, placePrediction });
                if (!selection) return;

                onPlaceSelectedRef.current(selection);
            } catch (e: unknown) {
                logErrorForDev("LocationPlaceSearch.handleSelect", e);
            }
        };

        const handleSelectListener: EventListener = (evt) => {
            void handleSelect(evt as google.maps.places.PlacePredictionSelectEvent);
        };

        element.addEventListener("gmp-select", handleSelectListener);

        return () => {
            element.removeEventListener("gmp-select", handleSelectListener);
            element.removeEventListener("focusin", handleFocusIn);
            element.removeEventListener("focusout", handleFocusOut);
            element.remove();
            autocompleteRef.current = null;
        };
    }, [places, countryRestrictions, disabled]);

    useEffect(() => {
        const element = autocompleteRef.current;
        if (!element || userEditingSearchRef.current) return;
        if (element.value !== searchDisplayValue) {
            element.value = searchDisplayValue;
        }
    }, [searchDisplayValue]);

    return (
        <Form.Group>
            <Form.Label htmlFor="location-place-search">Search for address or place</Form.Label>
            <div
                ref={containerRef}
                id="location-place-search"
                className={disabled ? "pe-none opacity-50" : undefined}
                aria-disabled={disabled}
            />
        </Form.Group>
    );
};

export { LocationPlaceSearch };
